import type { Edge, Node } from '../../../core/models/scene';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import { isPersonNodeType, resolveOrgNodeType } from '../fields/entityFieldPacks';

/**
 * Succession-projection enrichment for record-backed person charts.
 *
 * Person org charts usually render people only — the durable Position records
 * derived from the folder source have no canvas nodes. Succession drafting,
 * however, targets record-backed Positions. This helper projects that
 * occupancy into the succession graph. A fully visible canonical model
 * replaces record-backed canvas occupancy; partial/redacted models only fill
 * missing edges. Virtual Position nodes carry only the record UUID and label.
 *
 * The virtual nodes live exclusively in the succession projection consumed by
 * the property panel — they are never written to the store or the shared
 * Y.Doc.
 */

export const VIRTUAL_POSITION_NODE_PREFIX = 'virtual-position:';

export type SuccessionOccupancyModel = Pick<
  OrgModelProjection,
  'positions' | 'positionState' | 'occupancyVisibility'
>;

export type SuccessionKeyPositionModel = Pick<
  OrgModelProjection,
  'people' | 'positions' | 'positionState'
>;

/**
 * Projects the effective Key Position fact onto Position references already
 * used by succession planning. Position-owned truth wins; the occupant value
 * remains a compatibility fallback for people-first imports created before
 * Key Position ownership moved to Position.
 */
export function keyPositionByPositionRef(
  model: SuccessionKeyPositionModel | undefined,
): ReadonlyMap<string, boolean> {
  const result = new Map<string, boolean>();
  if (!model) return result;

  const peopleByRef = entitiesByReference(model.people);
  const positionsByRef = entitiesByReference(model.positions);
  for (const state of model.positionState) {
    const position = positionsByRef.get(state.positionId);
    if (!position) continue;
    const positionValue = booleanValue(position.props.keyPosition);
    const occupantRef = state.primaryOccupantPersonId
      ?? (state.occupantPersonIds.length === 1 ? state.occupantPersonIds[0] : null);
    const occupantValue = occupantRef
      ? booleanValue(peopleByRef.get(occupantRef)?.props.keyPosition)
      : undefined;
    const keyPosition = positionValue ?? occupantValue;
    if (keyPosition === undefined) continue;
    for (const ref of entityReferences(position.id, position.externalId)) {
      result.set(ref, keyPosition);
    }
  }
  return result;
}

export function augmentSuccessionGraphWithRecordOccupancy(
  nodes: Node[],
  edges: Edge[],
  model: SuccessionOccupancyModel | undefined,
): { nodes: Node[]; edges: Edge[] } {
  if (!model || (model.positionState.length === 0 && model.occupancyVisibility !== 'visible')) {
    return { nodes, edges };
  }

  const positionLabelById = new Map(model.positions.map((position) => [position.id, position.label]));
  const positionIdsByOccupant = new Map<string, string[]>();
  for (const state of model.positionState) {
    for (const occupant of state.occupantPersonIds) {
      positionIdsByOccupant.set(occupant, [...(positionIdsByOccupant.get(occupant) ?? []), state.positionId]);
    }
  }

  const nodeIdByRecordId = new Map<string, string>();
  for (const node of nodes) {
    const recordId = recordIdOf(node);
    if (recordId && !nodeIdByRecordId.has(recordId)) nodeIdByRecordId.set(recordId, node.id);
  }
  const recordBackedPersonNodeIds = new Set(nodes.flatMap((node) => (
    isPersonNodeType(resolveOrgNodeType(node)) && recordIdOf(node) ? [node.id] : []
  )));
  const canonicalOccupancyIsAuthoritative = model.occupancyVisibility === 'visible';
  const canvasPositionIdByPersonNodeId = new Map(
    edges.filter(isOccupiesEdge).map((edge) => [edge.from, edge.to] as const),
  );
  const baseEdges = canonicalOccupancyIsAuthoritative
    ? edges.filter((edge) => !(isOccupiesEdge(edge) && recordBackedPersonNodeIds.has(edge.from)))
    : edges;
  const occupiedPersonNodeIds = new Set(baseEdges.filter(isOccupiesEdge).map((edge) => edge.from));

  const virtualNodesById = new Map<string, Node>();
  const virtualEdges: Edge[] = [];
  const movedPersonNodeIds = new Set<string>();
  for (const node of nodes) {
    if (!isPersonNodeType(resolveOrgNodeType(node))) continue;
    if (occupiedPersonNodeIds.has(node.id)) continue;
    const personRecordId = recordIdOf(node);
    if (!personRecordId) continue;
    const positionRecordIds = positionIdsByOccupant.get(personRecordId) ?? [];
    const canvasPositionId = canvasPositionIdByPersonNodeId.get(node.id);
    if (positionRecordIds.length !== 1) {
      if (
        canonicalOccupancyIsAuthoritative
        && canvasPositionId
        && hasLegacySuccessionHint(node)
      ) {
        movedPersonNodeIds.add(node.id);
      }
      continue;
    }
    const positionRecordId = positionRecordIds[0];

    const existingNodeId = nodeIdByRecordId.get(positionRecordId);
    const canonicalPositionNodeId = existingNodeId ?? `${VIRTUAL_POSITION_NODE_PREFIX}${positionRecordId}`;
    if (
      canonicalOccupancyIsAuthoritative
      && canvasPositionId
      && canvasPositionId !== canonicalPositionNodeId
      && hasLegacySuccessionHint(node)
    ) {
      movedPersonNodeIds.add(node.id);
    }
    if (existingNodeId) {
      if (existingNodeId === node.id) continue;
      virtualEdges.push(virtualOccupiesEdge(node.id, existingNodeId));
      continue;
    }

    const virtualNodeId = `${VIRTUAL_POSITION_NODE_PREFIX}${positionRecordId}`;
    if (!virtualNodesById.has(virtualNodeId)) {
      const label = positionLabelById.get(positionRecordId) ?? 'Position';
      virtualNodesById.set(virtualNodeId, {
        id: virtualNodeId,
        type: 'org_position',
        name: label,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        attrs: {
          folderRecordId: positionRecordId,
          title: label,
          name: label,
          recordProjection: true,
        },
      } as Node);
    }
    virtualEdges.push(virtualOccupiesEdge(node.id, virtualNodeId));
  }

  const projectedNodes = movedPersonNodeIds.size === 0
    ? nodes
    : nodes.map((node) => (
      movedPersonNodeIds.has(node.id) ? withoutLegacySuccessionHint(node) : node
    ));
  if (virtualNodesById.size === 0 && virtualEdges.length === 0) {
    if (baseEdges === edges && projectedNodes === nodes) return { nodes, edges };
    return { nodes: projectedNodes, edges: baseEdges };
  }
  return {
    nodes: [...projectedNodes, ...virtualNodesById.values()],
    edges: [...baseEdges, ...virtualEdges],
  };
}

/**
 * Maps each Position record ref (`record:<id>`) to the canvas node of the
 * person occupying it.
 *
 * Canvas consumers never receive the virtual Position nodes above, so a
 * succession nomination targeting a durable Position record has nothing to
 * attach to on a person chart. This lets those consumers fall back to the
 * incumbent's node — the same person the property panel treats as the role's
 * holder. Positions with anything other than exactly one occupant are omitted:
 * there is no single node to draw against.
 */
export function occupantNodeIdByPositionRef(
  nodes: Node[],
  model: SuccessionOccupancyModel | undefined,
): ReadonlyMap<string, string> {
  const result = new Map<string, string>();
  if (!model || model.positionState.length === 0) return result;

  const nodeIdByRecordId = new Map<string, string>();
  for (const node of nodes) {
    const recordId = recordIdOf(node);
    if (recordId && !nodeIdByRecordId.has(recordId)) nodeIdByRecordId.set(recordId, node.id);
  }

  for (const state of model.positionState) {
    if (state.occupantPersonIds.length !== 1) continue;
    const occupantNodeId = nodeIdByRecordId.get(state.occupantPersonIds[0]);
    if (occupantNodeId) result.set(`record:${state.positionId}`, occupantNodeId);
  }
  return result;
}

function virtualOccupiesEdge(fromNodeId: string, toNodeId: string): Edge {
  return {
    id: `virtual-occupies:${fromNodeId}:${toNodeId}`,
    type: 'occupies' as Edge['type'],
    from: fromNodeId,
    to: toNodeId,
    attrs: { relation: 'occupies', predicate: 'occupies', recordProjection: true },
  } as Edge;
}

function recordIdOf(node: Node): string | null {
  const value = node.attrs?.folderRecordId;
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function entitiesByReference<T extends { id: string; externalId: string | null }>(
  entities: readonly T[],
): ReadonlyMap<string, T> {
  const result = new Map<string, T>();
  for (const entity of entities) {
    for (const ref of entityReferences(entity.id, entity.externalId)) result.set(ref, entity);
  }
  return result;
}

function entityReferences(id: string, externalId: string | null): readonly string[] {
  return [...new Set([
    id,
    `record:${id}`,
    ...(externalId ? [externalId, `record:${externalId}`] : []),
  ])];
}

function booleanValue(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return undefined;
}

function withoutLegacySuccessionHint(node: Node): Node {
  const source = node as Node & Record<string, unknown>;
  const {
    readyNowSuccessor: _readyNowSuccessor,
    readyNowSuccessors: _readyNowSuccessors,
    ...withoutDirectHints
  } = source;
  return {
    ...withoutDirectHints,
    attrs: withoutHintFields(node.attrs),
    ...(node.props ? { props: withoutHintFields(node.props) } : {}),
  } as Node;
}

function hasLegacySuccessionHint(node: Node): boolean {
  const direct = node as Node & Record<string, unknown>;
  return direct.readyNowSuccessor !== undefined
    || direct.readyNowSuccessors !== undefined
    || node.attrs?.readyNowSuccessor !== undefined
    || node.attrs?.readyNowSuccessors !== undefined
    || node.props?.readyNowSuccessor !== undefined
    || node.props?.readyNowSuccessors !== undefined;
}

function withoutHintFields(
  values: Readonly<Record<string, unknown>> | undefined,
): Record<string, unknown> | undefined {
  if (!values) return values;
  const {
    readyNowSuccessor: _readyNowSuccessor,
    readyNowSuccessors: _readyNowSuccessors,
    ...remaining
  } = values;
  return remaining;
}

function isOccupiesEdge(edge: Edge): boolean {
  if (String(edge.type) === 'occupies') return true;
  const relation = edge.attrs?.predicate ?? edge.attrs?.relation;
  return typeof relation === 'string' && relation === 'occupies';
}
